// Shared bulk delete engine for the bulk listing screens (client quotes, provider
// quotes, 3P quotes). Extracted from BulkQuotesList.vue in MOOV-5261.
//
// This is a LOCAL mixin: import it explicitly in the component that needs it.
// It is deliberately NOT registered with Vue.mixin() — see docs/mixins-namespace.md.
//
// What it gives you: a bounded pool of workers over a shared cursor (no per-chunk
// barrier), retry with exponential backoff + jitter, 404 treated as success,
// progress accumulated outside the reactive system and flushed on a timer,
// cooperative cancellation, per-row error reporting with retry and CSV export,
// and an in-place refresh when the run ends.
//
// What the component must provide:
//   deleteStepsFor(row)          REQUIRED. Ordered steps for one row, each
//                                {name, counter, run: () => Promise}.
//   exportToCsv(filename, csv)   Already present in every bulk component.
//
// The rest is declared as data, not overridden as methods — keeping it declarative
// is what stops every consumer from carrying its own near-identical copy of the
// same five one-liners:
//   deletePhaseKeys              Counter keys tracked for the phase breakdown.
//   deletePhaseLabels            {counterKey: translated label}.
//   deleteRowLabelField          Row field used as the label (and as the CSV's
//                                second column): 'name', 'reference', ...
//   deleteErrorCsvIdColumn       Name of the CSV's id column.
//   deleteErrorsCsvName          Prefix of the failure export filename.
//   deleteOrphanWarning          Only multi-step chains can orphan children.
//
// Any of the hook methods can still be overridden when a consumer needs something
// the declarative form cannot express — the engine always calls them via `this.*`.
//
// Everything is resolved through `this.*`, so a component definition always wins
// over the default below — the same dispatch the rest of the repo relies on.
import Papa from 'papaparse'

export default {
  data() {
    return {
      deleting: false,
      // asymptote measured in MOOV-5255 is ~60-70 req/s; HTTP/3 has no 6-connection
      // ceiling, so the pool size is the knob and it lives in data to stay tunable
      deleteConcurrency: 60,
      deleteTotal: 0,
      deleteDone: 0,
      deletePhaseCounts: {},
      deletePhaseKeys: [],
      deletePhaseLabels: {},
      deleteRowLabelField: 'name',
      deleteErrorCsvIdColumn: 'id',
      deleteErrorsCsvName: 'delete',
      // only the multi-step chains can leave orphan children behind on a mid-chain
      // failure, so only they show the warning in the summary
      deleteOrphanWarning: false,
      deleteErrors: [],
      deleteCancelled: false,
      deleteStartedAt: null,
      deleteSummary: null
    }
  },
  computed: {
    // rate and ETA recompute on every deleteDone tick, so no extra timer is needed
    deleteRate() {
      if (!this.deleteStartedAt || this.deleteDone === 0) {
        return 0
      }
      const elapsed = (Date.now() - this.deleteStartedAt) / 1000
      return elapsed > 0 ? this.deleteDone / elapsed : 0
    },
    deleteRateLabel() {
      return this.deleteRate ? this.deleteRate.toFixed(1) : '—'
    },
    deleteEtaLabel() {
      const rate = this.deleteRate
      const remaining = this.deleteTotal - this.deleteDone
      if (!rate || remaining <= 0) {
        return '—'
      }
      const seconds = Math.ceil(remaining / rate)
      if (seconds < 60) {
        return seconds + 's'
      }
      return Math.floor(seconds / 60) + 'm ' + ('0' + (seconds % 60)).slice(-2) + 's'
    },
    // reading through a computed means the template never has to guard against a
    // phase key that has not been initialised yet
    deletePhases() {
      return this.deletePhaseKeys.map(key => {
        return {key, label: this.deletePhaseLabel(key), count: this.deletePhaseCounts[key] || 0}
      })
    },
    // every consumer wires BulkDeleteProgress the same way, so the binding lives
    // here and the call site stays a single line:
    //   <bulk-delete-progress v-bind="bulkDeleteProps" v-on="bulkDeleteListeners" />
    // spelling it out per component would be the duplication this extraction removes
    bulkDeleteProps() {
      return {
        deleting: this.deleting,
        total: this.deleteTotal,
        done: this.deleteDone,
        phases: this.deletePhases,
        rateLabel: this.deleteRateLabel,
        etaLabel: this.deleteEtaLabel,
        cancelled: this.deleteCancelled,
        summary: this.deleteSummary,
        errors: this.deleteErrors,
        orphanWarning: this.deleteOrphanWarning
      }
    },
    bulkDeleteListeners() {
      return {
        cancel: this.cancelDelete,
        retry: this.retryFailedDeletes,
        export: this.exportFailedDeletes,
        dismiss: () => { this.deleteSummary = null }
      }
    }
  },
  beforeDestroy() {
    // navigating away mid-delete destroys the component while the pool is still
    // running; without this the flush interval would keep firing on it
    this.stopDeleteProgressTimer()
  },
  methods: {
    // single entry point for both the initial delete and the retry of failed rows
    async runDeleteBatch(entries) {
      if (entries.length === 0) {
        return
      }
      this.deleting = true
      // the table is destroyed while deleting (v-if), so the fresh instance
      // never emits row-selected([]); without this reset Backup Selected
      // would stay enabled over a selection of already-deleted rows
      this.selected = []
      this.deleteCancelled = false
      this.deleteErrors = []
      this.deleteSummary = null
      this.deleteTotal = entries.length
      this.deleteDone = 0
      this.deletePhaseCounts = this.buildDeletePhaseCounts()
      this.deleteStartedAt = Date.now()
      // The counts live outside data() on purpose, so they are NOT reactive:
      // bumping them per step used to re-render the component — and with it the
      // 500-row table, whose cells resolve their labels with findIndex+find —
      // roughly 2000 times per run, which jammed the main thread and starved
      // the delete requests. Flushing on an interval bounds renders by elapsed
      // time instead of by the amount of work.
      this._deleteTally = this.buildDeletePhaseCounts()
      this._deleteTally.done = 0
      this._deleteFlushTimer = setInterval(this.flushDeleteProgress, 250)
      try {
        await this.runDeletePool(entries)
      } finally {
        this.stopDeleteProgressTimer()
        // flush before the summary reads deleteDone, or it would report fewer
        // deleted rows than there were by up to one flush interval
        this.flushDeleteProgress()
        this.deleteSummary = {
          deleted: this.deleteDone - this.deleteErrors.length,
          failed: this.deleteErrors.length,
          skipped: this.deleteTotal - this.deleteDone,
          cancelled: this.deleteCancelled
        }
        this.deleting = false
        // the backup no longer matches what is on screen, so require a fresh one
        this.backuped = false
        try {
          await this.refreshAfterDelete()
        } catch (e) {
          console.error(e)
        }
      }
    },
    buildDeletePhaseCounts() {
      const counts = {}
      this.deletePhaseKeys.forEach(key => {
        counts[key] = 0
      })
      return counts
    },
    // one reactive notification per flush instead of one per step; assigning a
    // fresh deletePhaseCounts object beats bumping its keys one by one
    flushDeleteProgress() {
      const tally = this._deleteTally
      if (!tally) {
        return
      }
      this.deleteDone = tally.done
      const counts = {}
      this.deletePhaseKeys.forEach(key => {
        counts[key] = tally[key] || 0
      })
      this.deletePhaseCounts = counts
    },
    stopDeleteProgressTimer() {
      if (this._deleteFlushTimer) {
        clearInterval(this._deleteFlushTimer)
        this._deleteFlushTimer = null
      }
    },
    // bounded pool: workers pull from a shared cursor with no per-chunk barrier,
    // so a slow row never holds back the rest
    async runDeletePool(entries) {
      let cursor = 0
      const worker = async () => {
        while (cursor < entries.length && !this.deleteCancelled) {
          const entry = entries[cursor++]
          try {
            await this.deleteRow(entry.row, entry.fromStep)
          } catch (error) {
            const identity = this.deleteRowIdentity(entry.row)
            this.deleteErrors.push({
              rowId: identity.id,
              rowLabel: identity.label,
              step: error.step,
              message: error.message,
              row: entry.row
            })
          }
          this._deleteTally.done++
        }
      }
      const workers = []
      const size = Math.min(this.deleteConcurrency, entries.length)
      for (let i = 0; i < size; i++) {
        workers.push(worker())
      }
      // the workers never reject, so Promise.all is enough — Promise.allSettled is
      // not polyfilled by the corejs2 preset this build uses
      await Promise.all(workers)
    },
    async deleteRow(row, fromStep) {
      const steps = this.deleteStepsFor(row)
      // a retry resumes at the step that failed; earlier steps are already done
      const resumeAt = fromStep ? steps.findIndex(step => { return step.name === fromStep }) : 0
      for (let i = resumeAt === -1 ? 0 : resumeAt; i < steps.length; i++) {
        const step = steps[i]
        try {
          await step.run()
        } catch (originalError) {
          const message = (originalError && originalError.body && originalError.body.message) ||
            (originalError && originalError.statusText) ||
            this.$gettext('Delete request failed')
          const error = new Error(message)
          error.step = step.name
          throw error
        }
        this._deleteTally[step.counter]++
      }
    },
    async deleteRequest(method, path, body) {
      const url = this.$appConfig.api.baseUrl + path
      let attempt = 0
      while (true) {
        try {
          return method === 'post'
            ? await this.$http.post(url, body)
            : await this.$http.delete(url)
        } catch (error) {
          const status = error && error.status
          // the resource is already gone: nothing left to do for this step
          if (status === 404) {
            return null
          }
          const retriable = status === 429 || status >= 500 || status === 0
          if (!retriable || attempt >= 2) {
            throw error
          }
          // jitter keeps the whole pool from retrying on the same millisecond,
          // which would turn a single 429 into a synchronized stampede.
          // crypto rather than Math.random(): the spread is not security
          // sensitive, but this runs only on the rare retry path, so the
          // stronger generator costs nothing and keeps the scanners quiet
          const spread = window.crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296
          const base = 300 * Math.pow(3, attempt)
          await new Promise(resolve => { return setTimeout(resolve, base * (0.5 + spread)) })
          attempt++
        }
      }
    },
    cancelDelete() {
      this.deleteCancelled = true
    },
    async retryFailedDeletes() {
      const entries = this.deleteErrors.map(error => { return {row: error.row, fromStep: error.step} })
      await this.runDeleteBatch(entries)
    },
    exportFailedDeletes() {
      const rows = this.deleteErrors.map(error => { return this.deleteErrorCsvRow(error) })
      this.exportToCsv(this.deleteErrorsCsvFilename(), Papa.unparse(rows))
    },
    // entry point shared by every consumer: the confirm() guard and the snapshot
    // of the selection are the same everywhere, only deleteStepsFor differs.
    // The Set dedups a selection that holds the same row twice — deleting it again
    // would only answer 404, but it would inflate the total
    async deleteSelected() {
      if (
        !confirm(this.$gettext('Are you sure you want to DELETE the selected rows?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))
      ) {
        return
      }
      const rows = [...new Set(this.selected)]
      await this.runDeleteBatch(rows.map(row => { return {row, fromStep: null} }))
    },
    // stay on the screen: reload the current result set in place instead of
    // pushing the router back to the listing
    refreshAfterDelete() {
      if (this.filter && this.filter.ids && this.filter.ids.length > 0) {
        return this.searchImportedQuotes(this.filter.ids)
      }
      return this.searchQuotes()
    },
    // --- hooks: defaults driven by the declarative data above ---
    deleteStepsFor(row) {
      throw new Error('bulkDeletePool.mixin: the component must implement deleteStepsFor(row)')
    },
    deleteRowIdentity(row) {
      return {id: row.id, label: row[this.deleteRowLabelField]}
    },
    deletePhaseLabel(key) {
      return this.deletePhaseLabels[key] || key
    },
    deleteErrorsCsvFilename() {
      return this.deleteErrorsCsvName + '_delete_errors.csv'
    },
    deleteErrorCsvRow(error) {
      const row = {}
      row[this.deleteErrorCsvIdColumn] = error.rowId
      row[this.deleteRowLabelField] = error.rowLabel
      row.step = error.step
      row.message = error.message
      return row
    }
  }
}
